var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { Suspense, useRef } from 'react';
import { isEmpty } from 'lodash-es';
import { twMerge } from 'tailwind-merge';
import { useEffect, useState, useCallback } from 'preact/hooks';
import LaunchButton from './components/LaunchButton';
import WidgetConvo from './components/WidgetConvo';
import { getComplaintWorkflow } from '../utils/Flows';
import { getURLParamValue } from '../../clearstep/util/AppHelpers';
import { getWorkflows } from '../../../api/Widget';
import { THEME } from '../../clearstep/util/Theme';
import { WidgetResizer } from './components/WidgetResizer';
import CollapsedGreeting from './components/CollapsedGreeting';
import { getComplaintParam, hasDefaultExpandedParam } from '../utils/Params';
import { MessageType, logEvent } from '../../../util/EventLog';
import { useAutoExpandCollapseSequence, useWidgetInitialState } from '../utils/WidgetStateHooks';
import ExpandedState from './components/ExpandedState/ExpandedState';
import { trackEvent } from '../../clearstep/util/Split';
export var WidgetStates;
(function (WidgetStates) {
    WidgetStates["HIDDEN"] = "HIDDEN";
    WidgetStates["COLLAPSED"] = "COLLAPSED";
    WidgetStates["EXPANDED"] = "EXPANDED";
})(WidgetStates || (WidgetStates = {}));
export var MobileStates;
(function (MobileStates) {
    MobileStates["MOBILE"] = "MOBILE";
    MobileStates["DESKTOP"] = "DESKTOP";
    MobileStates["UNKNOWN"] = "UNKNOWN";
})(MobileStates || (MobileStates = {}));
export var WidgetStateChangeType;
(function (WidgetStateChangeType) {
    WidgetStateChangeType["AUTO"] = "AUTO";
    WidgetStateChangeType["MANUAL"] = "MANUAL";
    WidgetStateChangeType["SET_DEFAULT_STATE"] = "SET_DEFAULT_STATE";
})(WidgetStateChangeType || (WidgetStateChangeType = {}));
export var Widget = function (props) {
    var _a, _b, _c;
    var sessionId = props.sessionId;
    var _d = useState(WidgetStates.HIDDEN), widgetState = _d[0], _setWidgetState = _d[1];
    var widgetPrevState = useRef(WidgetStates.HIDDEN);
    var _e = useState(MobileStates.UNKNOWN), mobileState = _e[0], setMobileState = _e[1];
    var _f = useState(false), isFeedbackOpen = _f[0], setIsFeedbackOpen = _f[1];
    var _g = useState(null), workflow = _g[0], _setWorkflow = _g[1];
    var _h = useState([]), workflows = _h[0], setWorkflows = _h[1];
    var _j = useState(false), isTransitioning = _j[0], setIsTransitioning = _j[1];
    var _k = useState(null), singleFlow = _k[0], setSingleFlow = _k[1];
    var initialGreeting = useRef({
        didShow: false,
        showing: false,
    });
    var userHasInteracted = useRef(false);
    // Widget.css transition .8s
    // Note this transition time is used in the iFrame that frames the widget, so we shouldn't
    // change without updating every client that implements the widget
    var transitionTime = 800;
    var smallDesign = true;
    var heightClass = smallDesign ? 'h-[265px]' : 'h-155';
    var widthClass = smallDesign ? 'w-56' : 'w-92';
    var borderRadius = smallDesign ? 'rounded-md' : 'rounded-2xl';
    useEffect(function () {
        var _a, _b, _c, _d;
        // Only show the initial greeting 1x, the first time we go into COLLAPSED state
        if (((_a = initialGreeting.current) === null || _a === void 0 ? void 0 : _a.didShow) === false &&
            ((_b = initialGreeting.current) === null || _b === void 0 ? void 0 : _b.showing) === false &&
            widgetState === WidgetStates.COLLAPSED) {
            initialGreeting.current.didShow = true;
            initialGreeting.current.showing = true;
        }
        if (((_c = initialGreeting.current) === null || _c === void 0 ? void 0 : _c.didShow) === true &&
            ((_d = initialGreeting.current) === null || _d === void 0 ? void 0 : _d.showing) === true &&
            widgetState !== WidgetStates.COLLAPSED) {
            initialGreeting.current.showing = false;
        }
    }, [widgetState]);
    useEffect(function () {
        var setPartnerWorkflows = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getWorkflows(THEME.partner)];
                    case 1:
                        response = _a.sent();
                        if (response && response.data && response.data.length > 0) {
                            setWorkflows(response.data);
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        setPartnerWorkflows();
    }, []);
    /*
      We can't use normal css media queries to determine isMobile b/c we need to determine
      based on parent window width (child is always going to be 'mobile' since
      the widget width is small)
    */
    useEffect(function () {
        var mobileMaxHeight = 715;
        var mobileMaxWidth = 490;
        var handleResizeMessage = function (e) {
            var w = e.data.parentWindowWidth;
            var h = e.data.parentWindowHeight;
            if (w && h) {
                var desktop = w > mobileMaxWidth && h > mobileMaxHeight;
                setMobileState(desktop ? MobileStates.DESKTOP : MobileStates.MOBILE);
            }
        };
        window.addEventListener('message', handleResizeMessage);
        return function () {
            window.removeEventListener('message', handleResizeMessage);
        };
    }, []);
    var setWidgetState = function (state, stateChangeType) {
        if (state === WidgetStates.EXPANDED &&
            widgetPrevState.current !== WidgetStates.EXPANDED &&
            (stateChangeType === WidgetStateChangeType.AUTO ||
                stateChangeType === WidgetStateChangeType.MANUAL)) {
            logEvent(MessageType.WIDGET_EXPANSION, {
                parentUrl: getURLParamValue('parentUrl'),
                widgetSessionId: sessionId === null || sessionId === void 0 ? void 0 : sessionId.current,
                expansionType: stateChangeType,
            });
            if (stateChangeType === WidgetStateChangeType.MANUAL) {
                trackEvent('user', 'widget_expansion', {
                    properties: {
                        parentUrl: getURLParamValue('parentUrl'),
                        widgetSessionId: sessionId === null || sessionId === void 0 ? void 0 : sessionId.current,
                        expansionType: stateChangeType,
                    },
                });
            }
        }
        if (state === WidgetStates.HIDDEN && stateChangeType === WidgetStateChangeType.MANUAL) {
            trackEvent('user', 'hide_widget', {
                properties: {
                    parentUrl: getURLParamValue('parentUrl'),
                    widgetSessionId: sessionId === null || sessionId === void 0 ? void 0 : sessionId.current,
                    expansionType: stateChangeType,
                },
            });
        }
        if (userHasInteracted.current === false && stateChangeType === WidgetStateChangeType.MANUAL) {
            userHasInteracted.current = true;
        }
        widgetPrevState.current = state;
        _setWidgetState(state);
    };
    // Determine widget initial state
    useWidgetInitialState({ mobileState: mobileState, setWidgetState: setWidgetState, userHasInteracted: userHasInteracted });
    // Set auto expand/collapse sequence
    useAutoExpandCollapseSequence({
        mobileState: mobileState,
        setWidgetState: setWidgetState,
        userHasInteracted: userHasInteracted,
        widgetState: widgetState,
    });
    useEffect(function () {
        setIsTransitioning(true);
        setTimeout(function () { return setIsTransitioning(false); }, transitionTime);
    }, [widgetState, workflow]);
    var setWorkflow = function (workflow, interactionType) {
        if (userHasInteracted.current === false && interactionType === WidgetStateChangeType.MANUAL) {
            userHasInteracted.current = true;
        }
        _setWorkflow(workflow);
    };
    // Auto Expand On initial load
    // Ensure we only auto expand once singleFlow is available (if param is set)
    var autoExpandOnLoad = useCallback(function () {
        if (hasDefaultExpandedParam(mobileState)) {
            var singleFlowName = getURLParamValue('singleFlow');
            if ((singleFlowName && singleFlow) || !singleFlowName) {
                setWidgetState(WidgetStates.EXPANDED, WidgetStateChangeType.AUTO);
            }
        }
    }, [singleFlow]);
    useEffect(function () {
        autoExpandOnLoad(); // this will fire only when autoExpandOnLoad-reference changes
    }, [autoExpandOnLoad]);
    useEffect(function () {
        var sfName = getURLParamValue('singleFlow');
        if (sfName && Array.isArray(workflows) && workflows.length > 0) {
            var sf = workflows.find(function (workflow) { return workflow.value === sfName; });
            if (sf) {
                var complaintParam = getComplaintParam();
                if (sfName === 'check_symptoms' && complaintParam) {
                    // Logic is a bit different because complaint workflow stored on
                    // the FE rather than for a button in flow/partners.go
                    var complaintWorkflow = getComplaintWorkflow(complaintParam, window.location.search);
                    setSingleFlow(complaintWorkflow);
                }
                else {
                    setSingleFlow(sf);
                }
            }
        }
    }, [workflows]);
    // Directly set the workflow to symptom check for singleFlow={flow_name}, skip over choose workflow screen
    useEffect(function () {
        if (widgetState === WidgetStates.EXPANDED && !isEmpty(singleFlow)) {
            setWorkflow(singleFlow, WidgetStateChangeType.AUTO);
        }
    }, [widgetState, singleFlow]);
    return (React.createElement(Suspense, { fallback: "" },
        React.createElement(WidgetResizer, { widgetState: widgetState, workflow: workflow, iconPath: (_a = THEME.widgetCustomizations) === null || _a === void 0 ? void 0 : _a.iconPath, mobileState: mobileState },
            React.createElement("div", { id: "contentPosition", className: twMerge('fixed max-w-full', mobileState === MobileStates.MOBILE && widgetState === WidgetStates.EXPANDED
                    ? 'bottom-0 right-0'
                    : 'bottom-5 right-5') },
                React.createElement("div", { id: "widget-container-internal" },
                    React.createElement("div", { id: "widget-content", className: twMerge('absolute right-0 box-border border-solid border-gray-200 bg-white shadow-widget-container transition-all duration-[800ms] ease-in-out', borderRadius, widgetState === WidgetStates.HIDDEN && 'h-0 w-0 border-0', widgetState === WidgetStates.COLLAPSED && 'h-24 w-56 rounded-md border', // max-w-[calc(100%-20px)]
                        widgetState === WidgetStates.EXPANDED &&
                            !workflow &&
                            mobileState !== MobileStates.MOBILE &&
                            "".concat(heightClass, " ").concat(widthClass), widgetState === WidgetStates.EXPANDED &&
                            workflow &&
                            mobileState !== MobileStates.MOBILE &&
                            'h-155 w-115', 
                        // !isMobile && 'max-h-[calc(100%-80px)]',
                        widgetState === WidgetStates.EXPANDED && mobileState === MobileStates.MOBILE
                            ? 'bottom-0 top-0 w-full'
                            : 'bottom-20', widgetState === WidgetStates.EXPANDED && !workflow && 'overflow-auto') },
                        widgetState === WidgetStates.COLLAPSED && !isTransitioning && (React.createElement(CollapsedGreeting, { setWidgetState: setWidgetState, displayInitialGreeting: (_b = initialGreeting.current) === null || _b === void 0 ? void 0 : _b.showing, iconPath: (_c = THEME.widgetCustomizations) === null || _c === void 0 ? void 0 : _c.iconPath })),
                        widgetState === WidgetStates.EXPANDED && !workflow && !isTransitioning && (React.createElement(ExpandedState, { mobileState: mobileState, setWorkflow: setWorkflow, setWidgetState: setWidgetState, isFeedbackOpen: isFeedbackOpen, setIsFeedbackOpen: setIsFeedbackOpen, userHasInteracted: userHasInteracted, sessionId: sessionId, workflows: workflows })),
                        widgetState === WidgetStates.EXPANDED && workflow && !isTransitioning && (React.createElement(WidgetConvo, { path: workflow.path, setWorkflow: setWorkflow, shortTitle: workflow.shortTitle, singleFlow: singleFlow, sessionId: sessionId === null || sessionId === void 0 ? void 0 : sessionId.current, setWidgetState: setWidgetState, mobileState: mobileState }))),
                    React.createElement(LaunchButton, { widgetState: widgetState, mobileState: mobileState, setWidgetState: setWidgetState, setWorkflow: setWorkflow }))))));
};
export default Widget;
